import { GzPolicyUiSettingsGridWebComponent } from '../../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/gridView';
import {
  Column,
  ColumnKeys,
  FILTERS,
  getGridColumnDetails,
  GRID_COLUMNS,
  PREDEFINED_FILTERS_KEYS,
} from './grid-config';
import { GzEcsAssignmentDataProvider } from './GzEcsAssignmentDataProvider';
import { EcsAssignment, EcsAssignmentFormValue } from '@nirvana/shared/dtos/types/policies/common';
import { WEB_COMPONENT_POLICY_PANEL_ENDPOINT_COMMUNICATION_ASSIGNMENT_GRID } from '../../../../../../../../webcomponents.constants';
import { customElement } from 'lit/decorators.js';
import { html, TemplateResult } from 'lit';
import { translateFn } from '../../../../../../../../utils/translator.helper';
import { GRID_ITEM_FORM_ID } from '../../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/base/component/GzPolicyUiSettingsBaseGrid';
import { ValidationResult } from '../../../../../../../../ui/gzForm/formField/types/form-validator';
import { MaxLengthValidator } from '../../../../../../../../ui/gzForm/validators/max-length.validator';
import { FontSizes, FontWeights } from '../../../../../../../../types/common/types';
import { RequiredValidator } from '../../../../../../../../ui/gzForm/validators/required.validator';
import { CustomizableValidator } from '../../../../../../../../ui/gzForm/validators/customizable.validator';
import { customIpValidation } from './validators';
import { GzEcsAssignmentItemFormWebComponent } from './GzEcsAssignmentItemFormWebComponent';
import { ButtonFontSizes, ButtonHeights, ButtonTypes } from 'apps/webcomponents/src/ui/gzButton/types';

export const MAX_CUSTOM_HOSTNAME_LENGTH = 110;

@customElement(WEB_COMPONENT_POLICY_PANEL_ENDPOINT_COMMUNICATION_ASSIGNMENT_GRID)
export class GzEcsAssignmentGridWebComponent extends GzPolicyUiSettingsGridWebComponent<
  Column,
  ColumnKeys,
  EcsAssignment,
  EcsAssignmentFormValue,
  GzEcsAssignmentDataProvider
> {
  protected allColumnDetails = getGridColumnDetails();
  protected columns = GRID_COLUMNS;
  protected showHeaderCustomActionsInsideGrid = false;

  protected hasFilters = true;
  protected filters = FILTERS;
  protected predefinedFiltersKeys = PREDEFINED_FILTERS_KEYS;

  protected hasPriorityColumn = true;

  protected translations = {
    editRow: translateFn('Edit'),
    deleteRow: translateFn('Delete'),
  };

  protected renderGridItemForm(): TemplateResult {
    return html`
      <gz-policy-panel-communication-endpoints-assignment-item
        style="padding-bottom: calc(var(--spacer))"
        id="${GRID_ITEM_FORM_ID}"
      ></gz-policy-panel-communication-endpoints-assignment-item>
    `;
  }

  protected renderEmptyStateGrid(): TemplateResult | null {
    return html`
      <gz-text fontSize="${FontSizes.HEADING_6}" fontWeight="${FontWeights.LIGHT}"
        >${translateFn('No assignments')}
      </gz-text>
      <gz-button
        id="empty-state-add-button"
        type="${ButtonTypes.QUIET}"
        buttonHeight="${ButtonHeights.SMALL}"
        buttonFontSize="${ButtonFontSizes.SMALL}"
        @click="${() => this.gridItemForm.focusFirstInput()}"
        >${translateFn('Adjust your filters or start adding Relays or Communication Servers.')}
      </gz-button>
    `;
  }

  protected getDataProvider(): GzEcsAssignmentDataProvider {
    return new GzEcsAssignmentDataProvider(this.initialGridItems);
  }

  protected isEditedRowChanged(oldItem: EcsAssignmentFormValue, newItem: EcsAssignmentFormValue): boolean {
    return (
      oldItem.customHostname !== newItem.customHostname ||
      oldItem.defaultPositionInGrid !== newItem.defaultPositionInGrid
    );
  }

  protected onEditRowEnabled(event: CustomEvent) {
    super.onEditRowEnabled(event);
    const { rowData } = event.detail;
    this.gridComponent.controller?.setValidatorsOnRowAndColumn(rowData.index, ColumnKeys.CUSTOM_HOST_NAME, [
      MaxLengthValidator(
        MAX_CUSTOM_HOSTNAME_LENGTH,
        translateFn('The custom hostname must not exceed {maxLength} characters.', {
          maxLength: MAX_CUSTOM_HOSTNAME_LENGTH,
        })
      ),
      RequiredValidator('Required field'),
    ]);
    this.gridComponent.controller?.setValidatorsOnRowAndColumn(rowData.index, ColumnKeys.IP, [
      RequiredValidator('Required field'),
      CustomizableValidator(customIpValidation),
    ]);
  }

  protected addGridItem(item: EcsAssignmentFormValue) {
    const { defaultPositionInGrid, ...gridItem } = item;
    super.addGridItem(gridItem, defaultPositionInGrid);
  }

  protected validateGridItemBeforeAdd(gridItemFormValue: EcsAssignmentFormValue): ValidationResult {
    const { customHostname, ip, protectedEntityId } = gridItemFormValue;

    // customHostname is required if ip is not provided
    if (!customHostname && ['-', 'N/A'].includes(ip)) {
      return { valid: false, error: translateFn('Custom hostname is required.') };
    }

    //get all items
    const items = (this.gridComponent.dataProvider as GzEcsAssignmentDataProvider).items;
    const customHostnameValue = customHostname.toLowerCase();

    //validate if customHostname or ip already exists
    for (const itemData of items) {
      if (customHostnameValue) {
        if (
          itemData.customHostname.toLowerCase() === customHostnameValue ||
          itemData.ip.toLowerCase() === customHostnameValue
        ) {
          return { valid: false, error: translateFn('The entry already exists.') };
        }
      } else if (itemData.protectedEntityId === protectedEntityId) {
        return { valid: false, error: translateFn('The entry already exists.') };
      }
    }
    return { valid: true };
  }

  protected validateGridItemBeforeEdit(
    oldItem: EcsAssignmentFormValue,
    newItem: EcsAssignmentFormValue
  ): ValidationResult {
    //skip if customHostname is not changed
    if (newItem.customHostname === oldItem.customHostname) {
      return { valid: true };
    }
    return this.validateGridItemBeforeAdd(newItem);
  }

  protected async onGridItemsChange() {
    super.onGridItemsChange();
    await this.updateComplete;
    (this.gridItemForm as GzEcsAssignmentItemFormWebComponent).resetPrioritySettings(
      (this.gridComponent.dataProvider as GzEcsAssignmentDataProvider).items.length + 1
    );
  }
}
